/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { Meta } from '@/data/Meta';
import { isB2BStore, useSettings } from '@/data/Settings';
import { useStoreURLKeyword } from '@/data/StoreURLKeyword';
import { useUser } from '@/data/User';
import { fetcher, getPageDataFromId } from '@/data/_PageDataFromId-Server';
import { DEFAULT_LANGUAGE, DEFAULT_META } from '@/data/config/DEFAULTS';
import { DATA_KEY_PAGE_DATA_FROM_ID } from '@/data/constants/dataKey';
import { Token } from '@/data/types/Token';
import { buildCanonicalUrl } from '@/data/utils/buildCanonicalUrl';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getIdFromPath } from '@/data/utils/getIdFromPath';
import { expand, shrink } from '@/data/utils/keyUtil';
import { normalizeStoreTokenPath } from '@/data/utils/normalizeStoreTokenPath';
import { setDefaultLayoutIfNeeded } from '@/data/utils/setDefaultLayoutIfNeeded';
import { useMemo } from 'react';
import useSWR from 'swr';
import { DYNAMIC_CONTENT_PAGE_FOLDER_TO_SEO_MAPPING } from './constants/pageDataFromId';
import { EMPTY_STRING } from '@/data/constants/marketing';

export { getPageDataFromId };

const OMIT_FOR_KEY = {
	relatedStores: true,
	locationInfo: true,
	contactInfo: true,
	defaultCatalog: true,
	userData: true,
	supportedLanguages: true,
	supportedCurrencies: true,
	storeName: true,
	state: true,
	ownerId: true,
	mapApiKey: true,
	inventorySystem: true,
	currencySymbol: true,
};
const EMPTY_TOKEN_CONTAINER: Token = {};


const metaDataMap = (contents: any): Meta =>
	[contents].reduce(
		(meta, item) => ({
			...meta,
			title: item?.page?.title || meta.title,
			description: item?.page?.metaDescription || meta.description,
			keywords: item?.page?.metaKeyword || meta.keywords,
		}),
		DEFAULT_META
	);

export const usePageDataFromId = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeUrlKeyword } = useStoreURLKeyword();
	const staticRoutes = useLocalization('Routes');
	const urlKeyword = storeUrlKeyword?.desktopURLKeyword;
	const {
		query: { path, searchTerm },
		locale = '',
	} = router;
	const { storeId, storeToken, langId } = getClientSideCommon(settings, router);

	const {
		urlKeywordName,
		isB2B,
		robotsExcludeNoFollowPages,
		robotsExcludeFollowPages
	} = useMemo(()=>{
		const { storeToken: { urlKeywordName = '' } = EMPTY_TOKEN_CONTAINER } = settings;
		return{
			isB2B: isB2BStore(settings),
			robotsExcludeNoFollowPages: settings.userData['RobotsExcludeNoFollowPages'],
			robotsExcludeFollowPages: settings.userData['RobotsExcludeFollowPages']+","+settings.userData['RobotsExcludeFollowPages1'],
			urlKeywordName
		}
	},[settings]);

	const iPath = useMemo(
		() => normalizeStoreTokenPath({ path, storeUrlKeyword: urlKeywordName }),
		[urlKeywordName, path]
	);
	const params = useExtraRequestParameters();
	const {
		data: _data,
		error,
		isLoading,
	} = useSWR(
		// user specific info is not needed to determine the route protection logic,
		// once the page landed on client, means it pass the server side validation.
		[
			shrink(
				{
					storeId,
					path: iPath,
					identifier: getIdFromPath(path, storeToken),
					localeId: langId || DEFAULT_LANGUAGE,
					...(searchTerm && { searchTerm: decodeURIComponent([searchTerm].flat().at(0) ?? '') }),
				},
				OMIT_FOR_KEY
			),
			DATA_KEY_PAGE_DATA_FROM_ID,
		],
		async ([props]) => fetcher(true)(expand(props), params),
		{
			revalidateOnFocus: false,
		}
	);
	
	const x_customField2 = useMemo(()=>_data?.x_customField2,[_data]);
	const parentFolderPath = DYNAMIC_CONTENT_PAGE_FOLDER_TO_SEO_MAPPING[x_customField2?.toLocaleLowerCase()??''];
	const tempPath = useMemo(()=>x_customField2 && x_customField2 !== EMPTY_STRING && !path?.includes(parentFolderPath)
								?[parentFolderPath, path??''].flat()
								:path,[path]);
	let canonical = useMemo(()=>buildCanonicalUrl({ path:tempPath, locale, urlKeywordName: urlKeyword, staticRoutes, pageType: _data?.page.type}),[_data]);
	// This is a condition which only satisfies home page. There is no canonical on home page 
	// without this code. 
	if(urlKeyword =="intertoys" && path == undefined) {		
		canonical = "/";
	} 
	const { data, meta } = useMemo(() => {
		const data = _data ? setDefaultLayoutIfNeeded(_data, isB2B) : _data;
		const meta = {
			...(data ? metaDataMap(data) : DEFAULT_META),
			...(canonical && { canonical }),
			robotsExcludeNoFollowPage: false,
			robotsExcludeFollowPage: false,
		};
		if (data?.page?.type === "SearchPage") {
			meta.title = "Search";
			meta.robotsExcludeNoFollowPage = true;
		}
		if(data?.tokenExternalValue){
			//NoFollowPages		
			meta.robotsExcludeNoFollowPage = robotsExcludeNoFollowPages?.split(',')?.filter(Boolean)?.includes(data.tokenExternalValue);
			// FollowPages
			meta.robotsExcludeFollowPage = robotsExcludeFollowPages?.split(',')?.filter(Boolean)?.includes(data.tokenExternalValue);
		}
		
		if (urlKeyword == "intertoys" && path == undefined) {
			meta.title = "Intertoys: ontdek speelgoed in onze online speelgoedwinkel!";
			meta.description = "Kom langs in de fysieke of online speelgoedwinkel van Intertoys en koop het leukste speelgoed voor jong en oud ✓ Click &amp; Collect ✓ Snel bezorgd";
			meta.keywords = "intertoys, speelgoed, speelgoedwinkel, intertoys speelgoedwinkel, online speelgoedwinkel, speelgoedwinkel intertoys, intertoys speelgoed, speelgoed kopen, speelgoed bestellen, leuk speelgoed, intertoys speelgoed";
		}
		if (data?.page.type.includes("StoreLocatorPage")) {
			meta.title = "Vind een speelgoedwinkel in de buurt! | Intertoys";
			meta.description = "Met Intertoys vind je altijd een speelgoedwinkel in de buurt. ✓Openingstijden & overige informatie ✓Speelgoedspecialist van NL";
			meta.keywords = "winkelzoeker intertoys, intertoys winkels, intertoys winkelzoeker, winkel zoeken, winkel zoeken intertoys";
		}
		if (data?.page.type.includes("StoreLocatorCityPage")) {
			if (path?.length == 2) {
				const metaWord = path[1].replace("speelgoedwinkel-", "").charAt(0).toUpperCase() + path[1].replace("speelgoedwinkel-", "").slice(1);
				meta.title = "Speelgoedwinkel " + metaWord + " | Intertoys";
				meta.description = "Op zoek naar een speelgoedwinkel in " + metaWord + "? Ontdek speelgoed op zijn best! Bekijk hier alle speelgoedwinkels in " + metaWord + " ✓ Click & Collect";
				meta.keywords = "Speelgoed " + metaWord + ", Speelgoedwinkels " + metaWord + ", " + metaWord + " speelgoedwinkel, Speelgoedzaak " + metaWord + " ";
			}
		}
		if (data?.page.type.includes("StoreLocatorStreetPage")) {
			if (path?.length == 3) {
				const metaWord = path[1].replace("speelgoedwinkel-", "").charAt(0).toUpperCase() + path[1].replace("speelgoedwinkel-", "").slice(1)+" "+
				path[2].charAt(0).toUpperCase() + path[2].slice(1);
				meta.title = metaWord + "| Intertoys";
				meta.description = "Kom vandaag nog langs bij Intertoys " + metaWord + ". Bekijk hier alle openingstijden en winkelinformatie. ✓ Click & Collect ✓ Enorme keuze in speelgoed";
				meta.keywords = "Intertoys " + metaWord;
			}
		}
		return { meta, data };
	}, [_data, canonical, isB2B]);
	return {
		data,
		meta,
		loading: isLoading,
		error,
	};
};